/**
 * clipboard.js — safe cross-browser copy-to-clipboard helper.
 *
 * Order of attempts:
 *   1. `navigator.clipboard.writeText` — modern, requires secure context or
 *      user activation.
 *   2. Legacy `document.execCommand('copy')` — fallback for iframes /
 *      insecure contexts / older browsers where the async API is blocked.
 *
 * Returns `true` on success, `false` otherwise (never throws).
 */
export async function copyText(text) {
  if (text == null) return false;
  const str = String(text);
  // 1) Modern async API
  try {
    if (navigator.clipboard && window.isSecureContext !== false) {
      await navigator.clipboard.writeText(str);
      return true;
    }
  } catch {
    /* fall through to execCommand */
  }
  // 2) Legacy execCommand
  try {
    const ta = document.createElement("textarea");
    ta.value = str;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}

/**
 * copyWithToast — copies text and pops a toast automatically. Handy for
 * one-liners on buttons. Pass `label` to customize the toast message.
 */
export async function copyWithToast(text, opts = {}) {
  const { label = "Copied", errorLabel = "Copy failed — please copy manually",
          toast } = opts;
  const ok = await copyText(text);
  if (toast) {
    if (ok) toast.success(label);
    else toast.error(errorLabel);
  }
  return ok;
}
